/**
 * Symbol Logo Utilities
 *
 * Normalizes symbols to the format used for logo storage in R2.
 * Logos are stored as `logos/{normalizedSymbol}.png`.
 */

import { theme } from "@/styles/theme";
import { CRYPTO_TICKERS } from "@/lib/market-hours";

/** Commodity symbols that need special FMP mapping */
const COMMODITY_FMP_MAP: Record<string, string> = {
  "XAU/USD": "XAUUSD",
  "XAG/USD": "XAGUSD",
  "WTI/USD": "CLUSD",
  "XBR/USD": "BZUSD",
  "XPT/USD": "XPTUSD",
  "XPD/USD": "XPDUSD",
};

/**
 * Symbols whose stored art is worse than a monogram.
 *
 * Most of the old reasons for this list are GONE since the 2026-09-01
 * normalization — baked-in white frames, opaque colour tiles and badly framed
 * wordmarks are all handled by `lib/logos/normalize` now, and MOD / USEG /
 * VINE were checked and deliberately NOT listed: trimmed onto a plate they
 * read better than a letter would.
 *
 * What remains is art that is genuinely EMPTY. That is the dangerous case,
 * because an empty PNG is served as a 200: `onError` never fires, `ensureLogo`
 * reports the object exists, and every surface draws a blank tile instead of
 * falling back. Found by scanning all 4,819 stored originals for an alpha mean
 * below 1 — re-run that scan rather than waiting to spot one by eye.
 */
const LOGO_FALLBACK_SET = new Set<string>([
  // Add a ticker here only if its stored art is genuinely worse than a
  // monogram — in practice, only if it is blank. A logo that is merely ugly or
  // oddly framed should be fixed in the normalizer, where it fixes every
  // symbol of that shape at once, not muted here one ticker at a time.
  "AXIA3", // no logo in the store; seen empty on the 2026-08-21 insider recap
  "ABUS",  // stored art is a fully transparent 512x512 PNG (alpha mean 0.00)
  "OFC",   // same — blank source, served as a valid 200
]);

export function shouldUseLogoFallback(symbol: string): boolean {
  return LOGO_FALLBACK_SET.has(normalizeSymbolForLogo(symbol));
}

/** Vibrant palette for fallback pills — deterministic per symbol */
const ICON_COLORS = [
  "#3B82F6", // blue
  "#22C55E", // green
  "#EF4444", // red
  "#F59E0B", // amber
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#F97316", // orange
  "#6366F1", // indigo
  "#14B8A6", // teal
];

/** Deterministic color for a symbol — shared between SymbolIcon (HTML) and drawLegend (canvas) */
export function getSymbolColor(symbol: string): string {
  let hash = 0;
  const s = symbol.toUpperCase();
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  return ICON_COLORS[Math.abs(hash) % ICON_COLORS.length];
}

/**
 * How a caller says "this bare ticker is a coin, not the company of the same
 * name". See `normalizeSymbolForLogo`.
 */
export interface LogoSymbolOptions {
  /** The symbol names a cryptocurrency. Only consulted for a bare ticker. */
  crypto?: boolean;
}

/**
 * Normalize a symbol to the format used for logo filenames.
 * - Strips `/` from crypto/forex/commodity pairs
 * - Uppercases
 * - Handles commodity-specific FMP mappings
 * - Maps a bare crypto ticker to its USD pair, but ONLY when told it is crypto
 *
 * **A bare ticker is genuinely ambiguous and the store proves it.** Measured
 * 2026-09-04: 19 of the 88 names in `CRYPTO_TICKERS` are also plain symbols in
 * the screener index — BTC, ETH, LTC, ATOM, COMP, ENS, AR, SUI, SEI, NEO,
 * CAKE, SNX and friends — and `logos/v6/` holds BOTH marks for every one of
 * them (`LTC.png` the company, `LTCUSD.png` the coin). So there is no rule that
 * reads a bare ticker and picks correctly; the surface has to say which it
 * means. Guessing from the ticker would put the Litecoin mark on a stock.
 *
 * This is why the flag is opt-in rather than a lookup: a caller that does not
 * know stays on the old behaviour, and only a surface rendering a known crypto
 * row asks for the pair.
 */
export function normalizeSymbolForLogo(
  symbol: string,
  opts?: LogoSymbolOptions,
): string {
  const upper = symbol.toUpperCase();
  // Check commodity map first (for special mappings like WTI/USD → CLUSD)
  if (COMMODITY_FMP_MAP[upper]) return COMMODITY_FMP_MAP[upper];
  // A bare coin ticker is stored under its USD pair (BTC → BTCUSD), which is
  // the spelling the crypto hydration path writes.
  if (opts?.crypto && !upper.includes("/") && CRYPTO_TICKERS.has(upper)) {
    return `${upper}USD`;
  }
  // Strip slashes for crypto (BTC/USD → BTCUSD) and forex (EUR/USD → EURUSD)
  return upper.replace("/", "");
}

/**
 * The logo key for ONE LEG of a ratio or spread (BTC/SOL, NVDA/SPY).
 *
 * Legs are typed bare, and a bare ticker is ambiguous (see
 * `normalizeSymbolForLogo`): bare SOL has no stored mark (the stock, a 404, so
 * the letter "S") and bare BTC is the Grayscale mini trust's mark. The leg's
 * DATA is already the coin, because `/api/chart-data` maps any bare ticker in
 * `CRYPTO_TICKERS` to its USD pair; this gives the logo the same rule, so the
 * mark always matches the bars. Never use it for a plain symbol: a chart of
 * the BTC ETF is the ETF.
 */
export function ratioLegLogoSymbol(leg: string): string {
  const upper = leg.toUpperCase();
  return !upper.includes("/") && CRYPTO_TICKERS.has(upper) ? `${upper}/USD` : upper;
}

/** Edge worker URL — serves logos directly from R2 at Cloudflare edge */
const EDGE_WORKER_URL = process.env.NEXT_PUBLIC_CHART_DATA_WORKER_URL || "";

/**
 * Cache-buster for logo URLs. Both resolvers answer `immutable, max-age=1y`,
 * so a stored logo that CHANGES is invisible to every browser and Cloudflare
 * edge until this moves.
 *
 * ONE constant, imported everywhere. It used to be a literal typed at each
 * call site, which drifted to `v=4` on four surfaces and `v=5` on three — so a
 * re-sync reached some surfaces and not others, and nobody could tell which.
 *
 * v6 = the normalized store (`logos/v6/`): one treatment for every mark
 * instead of the four the raw vendor art arrives in.
 */
export const LOGO_CACHE_VERSION = 6;

/**
 * Get the URL for a symbol's logo image.
 * Uses edge worker (Cloudflare CDN) when available, falls back to Vercel API route.
 */
export function getLogoUrl(symbol: string, opts?: LogoSymbolOptions): string {
  const normalized = normalizeSymbolForLogo(symbol, opts);
  if (EDGE_WORKER_URL) {
    return `${EDGE_WORKER_URL}/logo/${normalized}?v=${LOGO_CACHE_VERSION}`;
  }
  return `/api/logo/${encodeURIComponent(normalized)}?v=${LOGO_CACHE_VERSION}`;
}

/**
 * The monogram shown when a symbol has no usable mark.
 *
 * ONE definition, because there were four and they disagreed: a two-letter
 * white-on-colour pill in the watchlist, a ONE-letter pill of the same colour
 * on post cards, a grey bordered box on Recon, and a fourth painted on canvas
 * by the chart legend. Coverage is 94.2% as of 2026-09-01 and the 270 symbols
 * still without a mark are almost all long-tail coins, so this is now the
 * visible answer for a whole asset class rather than a rare edge case — it has
 * to look deliberate and identical everywhere.
 *
 * ONE letter, not two. Every surface that draws this puts the full ticker
 * immediately beside it, so "ST" next to "STLN" repeats a fact already on
 * screen, and a single letter still carries at the 16px Recon size where two
 * crowd the tile edge to edge.
 */
export interface SymbolMonogram {
  letter: string;
  background: string;
  color: string;
  fontSize: number;
  fontWeight: number;
  borderRadius: number;
}

export function symbolMonogram(symbol: string, size: number): SymbolMonogram {
  const normalized = normalizeSymbolForLogo(symbol);
  return {
    letter: normalized.slice(0, 1),
    background: getSymbolColor(normalized),
    color: theme.colors.text.primary,
    fontSize: Math.round(size * 0.5),
    fontWeight: 700,
    // Matches the normalized tile's own 0.22 corner radius, so a monogram and
    // a real mark sit at the same roundness in one column.
    borderRadius: Math.round(size * 0.22),
  };
}
